import { ChartColumn } from 'lucide-react';
import { minValue, required, useSourceContext, useTranslate } from 'ra-core';
import { useWatch } from 'react-hook-form';
import { ArrayInput } from '../../components/admin/array-input';
import { FileInput } from '../../components/admin/file-input';
import { ImageField } from '../../components/admin/image-field';
import { NumberInput } from '../../components/admin/number-input';
import { TextInput } from '../../components/admin/text-input';
import { ActionLink } from '../../components/custom/ActionLink';
import { CopyGrowAppLinkButton } from '../../components/custom/CopyGrowAppLinkButton';
import { UrlSourceInput } from '../../components/custom/UrlSourceInput';
import { maxFileSize } from '../../utils/validation';
import { MoonstarFormIterator } from '../Moonstar/MoonstarFormIterator';
import { MoonstarFormIteratorEntry } from '../Moonstar/MoonstarFormIteratorEntry';
import { SimpleStack } from '../Moonstar/SimpleStack';
import { ActivityTypeInput } from './ActivityTypeInput';

const imageValidation = maxFileSize('5MB');
const bannerValidation = [required(), imageValidation];
const minutesValidation = [required(), minValue(1)];

/**
 * The journey form inputs, shared by create and edit. On edit (`isEdit`), the section / activity ids are kept in
 * hidden inputs and saved activities get their "Copy link" / "Analytics" actions.
 */
export const JourneyInputs = ({ isEdit = false }: { isEdit?: boolean }) => (
  <>
    <SimpleStack>
      <TextInput source="name" validate={required()} />
      <TextInput
        multiline
        // Like the MUI `minRows={3}` (the shadcn textarea ignores `rows` and grows with its content)
        inputClassName="min-h-20"
        source="description"
        validate={required()}
      />
      <FileInput
        source="bannerImage"
        accept={{ 'image/*': ['.jpg', '.jpeg', '.png', '.gif'] }}
        validate={bannerValidation}
      >
        <ImageField
          source="src"
          title="title"
          className="[&_img]:max-h-40 [&_img]:object-contain"
        />
      </FileInput>
    </SimpleStack>
    <ArrayInput source="sections" className="max-w-[800px] min-w-[300px]">
      <MoonstarFormIterator
        addLabel="moonstar.journeys.addSection"
        collapsibleItems
      >
        <MoonstarFormIteratorEntry
          labelSource="name"
          placeholder="moonstar.journeys.section"
          defaultExpanded
        >
          {/*
            For each section / activity, we store their ID in a hidden text input. This is to ensure that the ID is preserved when the journey's contents are edited.
            If the user removes + re-adds a section / activity, it'll get a new ID and all the existing analytics will be disassociated from it. New IDs are generated in the backend.
          */}
          {isEdit && <TextInput source="id" className="hidden" />}
          <TextInput source="name" validate={required()} />
          <ArrayInput source="activities">
            <MoonstarFormIterator
              addLabel="moonstar.journeys.addActivity"
              collapsibleItems
            >
              <MoonstarFormIteratorEntry
                labelSource="name"
                placeholder="moonstar.journeys.activity"
                defaultExpanded
              >
                {isEdit && <TextInput source="id" className="hidden" />}
                <TextInput source="name" validate={required()} />
                <ActivityTypeInput source="thumbnailUrl" />
                <NumberInput
                  source="estimatedTimeToCompleteMinutes"
                  validate={minutesValidation}
                  step={1}
                  defaultValue={5}
                />
                <UrlSourceInput source="contentUrl" />
                {isEdit && <ActivityActions />}
              </MoonstarFormIteratorEntry>
            </MoonstarFormIterator>
          </ArrayInput>
        </MoonstarFormIteratorEntry>
      </MoonstarFormIterator>
    </ArrayInput>
  </>
);

function ActivityActions() {
  const sourceCtx = useSourceContext();
  const journeyId = useWatch({
    name: 'id',
  });

  /** `sections.{index}.activities.{index}.id` */
  const activityIdPath = sourceCtx.getSource('id');

  /** `sections.{index}` */
  const sectionPath = activityIdPath.substring(
    0,
    activityIdPath.indexOf('.activities')
  );

  const sectionId = useWatch({
    name: `${sectionPath}.id`,
  });
  const activityId = useWatch({ name: activityIdPath });
  const translate = useTranslate();

  if (!journeyId || !sectionId || !activityId) {
    // New journey / section / activity, so there's no available actions
    return null;
  }

  return (
    <div className="flex flex-wrap gap-1">
      <CopyGrowAppLinkButton
        path={`/journeys/${journeyId}?${new URLSearchParams({
          activityId,
          sectionId,
        })}`}
        variant="ghost"
        size="sm"
      />
      <ActionLink
        to={`/journeys/${journeyId}/sections/${sectionId}/activities/${activityId}/analytics`}
        variant="ghost"
        size="sm"
        icon={<ChartColumn />}
      >
        {translate('moonstar.analytics.name')}
      </ActionLink>
    </div>
  );
}
